import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { DownloadSimple, Paperclip, Trash } from "@phosphor-icons/react";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg";
const STATUS_VALUES = ["pending", "review", "issues", "reviewed"];

export function useRegulatoryAttachments(projectId, deviceId, module, onChange) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!projectId || !deviceId || !module) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/devices/${deviceId}/regulatory-documents`,
        { params: { module } }
      );
      if (requestId === requestSequence.current) {
        setDocuments(data || []);
        setError("");
      }
    } catch (e) {
      if (requestId === requestSequence.current) setError(formatApiError(e.response?.data?.detail));
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [projectId, deviceId, module]);

  useEffect(() => { load(); }, [load]);

  async function upload(recordId, file) {
    setError("");
    setBusy(`upload:${recordId}`);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("module", module);
      data.append("record_id", recordId);
      await api.post(`/projects/${projectId}/devices/${deviceId}/regulatory-documents`, data);
      await load();
      await onChange?.();
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      return false;
    } finally {
      setBusy("");
    }
  }

  async function updateStatus(id, review_status) {
    setBusy(id);
    setError("");
    try {
      await api.put(
        `/projects/${projectId}/devices/${deviceId}/regulatory-documents/${id}/status`,
        { review_status }
      );
      await load();
      await onChange?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function remove(id) {
    setBusy(id);
    setError("");
    try {
      await api.delete(`/projects/${projectId}/devices/${deviceId}/regulatory-documents/${id}`);
      await load();
      await onChange?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  async function download(document) {
    setBusy(document.id);
    setError("");
    try {
      const { data } = await api.get(
        `/projects/${projectId}/devices/${deviceId}/regulatory-documents/${document.id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = document.file_name;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  return { documents, loading, busy, error, setError, upload, updateStatus, remove, download, reload: load };
}

export default function RegulatoryAttachments({
  recordId,
  documents = [],
  loading = false,
  busy = "",
  error = "",
  upload,
  updateStatus,
  remove,
  download,
}) {
  const t = useT();
  const [pendingFile, setPendingFile] = useState(null);
  const [localError, setLocalError] = useState("");
  const relevant = documents.filter((document) => document.record_id === recordId);

  async function submitUpload() {
    if (!pendingFile) return;
    setLocalError("");
    if (pendingFile.size > 5 * 1024 * 1024) {
      setLocalError(t("reg.file_too_large"));
      return;
    }
    const uploaded = await upload(recordId, pendingFile);
    if (uploaded) setPendingFile(null);
  }

  return (
    <div className="space-y-3" data-testid={`reg-attachments-${recordId}`}>
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
        <label className="flex-1 min-w-0">
          <span className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{t("reg.attach_file")}</span>
          <input type="file" accept={ACCEPTED} onChange={(event) => setPendingFile(event.target.files?.[0] || null)}
            data-testid={`reg-file-${recordId}`}
            className="block w-full text-xs text-zinc-600 file:mr-3 file:border file:border-zinc-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-semibold hover:file:bg-zinc-50" />
          <span className="block text-[10px] text-zinc-400 mt-1">{t("reg.file_rules")}</span>
        </label>
        <button type="button" onClick={submitUpload} disabled={!pendingFile || busy !== ""}
          data-testid={`reg-upload-${recordId}`}
          className="inline-flex items-center justify-center gap-1 border border-blue-200 bg-blue-50 px-3 py-2 rounded-sm text-xs font-semibold text-blue-800 disabled:opacity-40">
          <Paperclip size={13} /> {busy === `upload:${recordId}` ? t("reg.uploading") : t("reg.upload")}
        </button>
      </div>
      {(localError || error) && <p className="text-xs text-red-700" role="alert">{localError || error}</p>}
      {loading ? (
        <div className="text-xs text-zinc-500">{t("reg.loading_documents")}</div>
      ) : relevant.length === 0 ? (
        <div className="border border-dashed border-zinc-200 rounded-sm p-3 text-xs text-zinc-500">{t("reg.no_documents")}</div>
      ) : (
        <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
          {relevant.map((document) => (
            <div key={document.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3" data-testid={`reg-document-${document.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-zinc-800 truncate">{document.file_name}</div>
                <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                  {(document.size / 1024).toFixed(0)} KB · {new Date(document.created_at).toLocaleDateString()}
                </div>
              </div>
              <select value={document.review_status || "pending"} disabled={busy === document.id}
                onChange={(event) => updateStatus(document.id, event.target.value)}
                aria-label={t("reg.document_status")}
                data-testid={`reg-document-status-${document.id}`}
                className="border border-zinc-200 bg-white px-2 py-1.5 rounded-sm text-xs">
                {STATUS_VALUES.map((status) => <option key={status} value={status}>{t(`reg.status.${status}`)}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <button type="button" title={t("reg.download")} onClick={() => download(document)} disabled={busy === document.id}
                  data-testid={`reg-document-download-${document.id}`}
                  className="p-2 text-blue-700 hover:bg-blue-50 rounded-sm disabled:opacity-40">
                  <DownloadSimple size={15} weight="bold" />
                </button>
                <button type="button" title={t("reg.delete_document")} onClick={() => {
                  if (window.confirm(t("reg.confirm_delete_document"))) remove(document.id);
                }} disabled={busy === document.id}
                  data-testid={`reg-document-delete-${document.id}`}
                  className="p-2 text-red-700 hover:bg-red-50 rounded-sm disabled:opacity-40">
                  <Trash size={15} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
